import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  TOUR_SHOWING_STATUS_FAILED,
  TOUR_SHOWING_STATUS_COMPLETED,
  TOUR_SHOWING_STATUS_WAITLIST,
} from 'common/tourConstants';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_PARTNER_STATS_TOURS,
} from 'common/dataTableConstants';

import {
  LEAD_ARCHIVE_ALL_REASONS_OPTION_ITEMS,
} from 'common/leadConstants';

import {
  dataTable,
  actionDataTableLoadItems,
  actionDataTableRemoveItem,
  actionDataTableReset,
  actionDataTableLoadNextPage,
} from 'client/helpers/dataTables';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';

import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';
import safeLocalStorage from 'client/lib/safeLocalStorage';

import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrTable from 'client/components/SrTable';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrModalV2, {
  MODAL_THEME_STACK,
} from 'client/components/SrModalV2/SrModalV2';
import SrFrontConversation from 'client/components/SrFrontConversation';
import SrArchiveListingLeadModal from 'client/components/SrArchiveListingLeadModal';
import SrTourListingFailedModal from 'client/components/SrTourListingFailedModal/SrTourListingFailedModal';

import ListingActions from 'client/actions/Listing';

function getDataTableSettings({
  listingManagementCompany,
  listingId,
  apartmentId,
  sinceDate,
  nonRentlinxLeads,
  tourStatus = TOUR_SHOWING_STATUS_COMPLETED,
}) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_TOURS,
    viewId: [
      'by',
      listingManagementCompany,
      listingId,
      apartmentId,
      sinceDate,
      nonRentlinxLeads,
      tourStatus,
    ].filter((x) => x).join('-'),
    sort: tourStatus === TOUR_SHOWING_STATUS_WAITLIST ? {
      'tours.createdDate': 'desc',
    } : {
      'tours.startDate': 'desc',
    },
    count: 50,
    customParams: {
      partnerStatsTours: {
        listingManagementCompany,
        listingId,
        apartmentId,
        sinceDate,
        nonRentlinxLeads,
        tourStatus,
      },
    },
  };

  return dataTableSettings;
}

const SrUnArchiveLeadModal = ({
  onClose,
  onSubmit,
}) => {
  return (
    <SrModalV2
      showCloseButton={false}
      contentMaxWidth='500px'
      contentPadding='30px 40px 30px 30px'
      theme={MODAL_THEME_STACK}
    >
      <SrText
        font='greycliff'
        weight='800'
        size='26'
        lineHeight='33px'
        align='center'
        width='100%'
        display='block'
      >
        Un-Archive Lead
      </SrText>
      <SrBox margin='32px 0'>
        <SrText
          font='greycliff'
          fontSize='18px'
          weight='500'
          display='block'
          align='center'
        >
          Are you sure you want to Un-Archive this lead?
        </SrText>
      </SrBox>
      <SrFlex justify='between'>
        <SrBox width='48%'>
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREY}
            type='button'
            onClick={onClose}
          >
            Cancel
          </SrButton>
        </SrBox>
        <SrBox width='48%'>
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREEN}
            onClick={onSubmit}
          >
            Un-Archive
          </SrButton>
        </SrBox>
      </SrFlex>
    </SrModalV2>
  );
};

@dataTable(getDataTableSettings)
@withInfiniteScroll({
  offset: 420,
})
@branch({
  currentUser: ['currentUser'],
})
class SrDataTablePartnerStatTours extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    onRefreshStats: PropTypes.func.isRequired,
    tourStatus: PropTypes.string,
    showMessagesColumn: PropTypes.bool,
  }

  constructor(props) {
    super(props);

    this.state = {
      tourListingId: null,
      archiveTourListingId: null,
      unArchiveTourListingId: null,
    };

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, getDataTableSettings(this.props));
    }
  }

  _onHideTourClick = (e) => {
    const { items, dispatch, onRefreshStats } = this.props;
    if (e.target.dataset.tourListingId) {
      const tourListingId = parseInt(e.target.dataset.tourListingId, 10);
      const { startDate, user } = items.find((item) => item.id === tourListingId);

      if (!global.confirm(
        'Are you sure you want to hide the item ' +
        `"${[
          user.name,
          user.email,
          user.phone,
          momentWithAustinTimezone(startDate).format('MM/DD/YYYY hh:mm A'),
        ].join(', ')}"?`
      )) {
        return;
      }

      dispatch(postWithState, {
        action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
        payload: {
          table: 'tourListings',
          where: {
            id: tourListingId,
          },
          updates: {
            hiddenFromStats: true,
          },
        },
        onSuccess: () => {
          dispatch(actionDataTableRemoveItem, {
            dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_TOURS,
            entityId: tourListingId,
          });
          onRefreshStats();
        },
      });
    }
  }

  _onShowBackupCode = (e) => {
    const { items } = this.props;
    const tourListingId = parseInt(e.target.dataset.tourListingId, 10) || null;

    if (tourListingId) {
      const { sunlockId } = items.find((item) => item.id === tourListingId);
      this.setState({ tourListingId });
      if (sunlockId) {
        safeLocalStorage.setItem('sunlockBackupCodeShown', tourListingId);
      }
    }
  }

  _onSubmitTourListingFailedModal = ({ tourListingStatus }) => {
    const { dispatch, onRefreshStats } = this.props;
    const { tourListingId } = this.state;

    this.setState({ tourListingId: null });

    if (tourListingStatus === TOUR_SHOWING_STATUS_COMPLETED) {
      dispatch(actionDataTableRemoveItem, {
        dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_TOURS,
        entityId: tourListingId,
      });

      dispatch(actionDataTableReset, {
        dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_TOURS,
      });

      onRefreshStats();
    }
  }

  _onOpenArchiveTourListingModal = (e) => {
    const archiveTourListingId = parseInt(e.target.dataset.tourListingId, 10);

    this.setState({ archiveTourListingId });
  }

  _onOpenUnArchiveTourListingModal = (e) => {
    const unArchiveTourListingId = parseInt(e.target.dataset.tourListingId, 10);

    this.setState({ unArchiveTourListingId });
  }

  _onCloseArchiveTourListingModal = () => {
    this.setState({ archiveTourListingId: null });
  }

  _onCloseUnArchiveTourListingModal = () => {
    this.setState({ unArchiveTourListingId: null });
  }

  _onArchiveTourListing = (formValues) => {
    const {
      dispatch,
      currentUser,
      items,
    } = this.props;
    const {
      archiveTourListingId,
    } = this.state;

    const { listing } = items.find((item) => item.id === archiveTourListingId);

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'tourListings',
        where: {
          id: archiveTourListingId,
        },
        updates: {
          archivedDate: new Date().toISOString(),
          archiveReasons: formValues.archiveReasons,
          archiveReasonsDescription: formValues.archiveReasonsDescription,
          otherArchiveReason: formValues.partnerArchiveReason || null,
          archivedByUserId: currentUser.id,
        },
      },
      onSuccess: () => {
        dispatch(actionDataTableLoadItems, {
          dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_TOURS,
          ...getDataTableSettings(this.props),
          filter: {
            'tourListings.id': archiveTourListingId,
          },
        });
        dispatch(ListingActions.getLeadHealth, {
          listingId: listing.id,
          forceRefresh: true,
        });
        this._onCloseArchiveTourListingModal();
      },
    });
  }

  _onUnArchiveTourListing = () => {
    const {
      dispatch,
      items,
    } = this.props;
    const {
      unArchiveTourListingId,
    } = this.state;

    const { listing } = items.find((item) => item.id === unArchiveTourListingId);

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'tourListings',
        where: {
          id: unArchiveTourListingId,
        },
        updates: {
          archivedDate: null,
          archiveReasons: null,
          otherArchiveReason: null,
          archivedByUserId: null,
        },
      },
      onSuccess: () => {
        dispatch(actionDataTableLoadItems, {
          dataSource: DATA_TABLE_SOURCE_PARTNER_STATS_TOURS,
          ...getDataTableSettings(this.props),
          filter: {
            'tourListings.id': unArchiveTourListingId,
          },
        });
        dispatch(ListingActions.getLeadHealth, {
          listingId: listing.id,
          forceRefresh: true,
        });
        this._onCloseUnArchiveTourListingModal();
      },
    });
  }

  _formatArchiveReasons = ({ archiveReasons, otherArchiveReason }) => {
    if (!archiveReasons?.length) {
      return '';
    }

    const reasons = lodashCompact(
      archiveReasons
        .filter((reason) => reason)
        .map((reason) => (
          LEAD_ARCHIVE_ALL_REASONS_OPTION_ITEMS
            .find((item) => item.value === reason)?.label
        ))
    ).join(', ');

    return (
      <>
        {reasons}
        {otherArchiveReason && (
          <>
            <br />
            <span style={{ maxWidth: 300, wordBreak: 'break-word' }}>
              &quot;{otherArchiveReason}&quot;
            </span>
          </>
        )}
      </>
    );
  }

  render() {
    const {
      items,
      onInfiniteScrollWrapperRef,
      tourStatus,
      dispatch,
      showMessagesColumn,
    } = this.props;
    const {
      tourListingId,
      archiveTourListingId,
      unArchiveTourListingId,
    } = this.state;

    const isWaitlist = tourStatus === TOUR_SHOWING_STATUS_WAITLIST;

    const headers = lodashCompact([
      { content: '#' },
      !isWaitlist && { content: 'archive reason' },
      { content: 'name' },
      { content: 'email' },
      { content: 'phone' },
      isWaitlist && { content: 'date' },
      !isWaitlist && { content: 'tour start date' },
      !isWaitlist && { content: 'tour type' },
      !isWaitlist && { content: 'agent name' },
      { content: 'address' },
      { content: 'links' },
      showMessagesColumn && { content: 'text' },
      { content: 'options' },
    ]);

    const rows = items.map(({
      id,
      archiveReasons,
      otherArchiveReason,
      tour,
      agentUser,
      user,
      listing,
      userFrontConversations,
    }, i) => {
      let optionsDOM = null;
      if (user.id && tourStatus === TOUR_SHOWING_STATUS_FAILED) {
        if (listing.sunlockId || listing.codeboxLockId) {
          optionsDOM = (
            <SrText
              color='blue300'
              cursor='pointer'
              onClick={this._onShowBackupCode}
              data-tour-listing-id={id}
            >
              {listing.sunlockId ? 'Backup Code' : 'Generate Code'}
            </SrText>
          );
        }
        else {
          optionsDOM = (
            <SrText>
              No Sunlock Assigned
            </SrText>
          );
        }
      }
      else if (user.id) {
        optionsDOM = (
          <>
            <SrText
              color='blue300'
              cursor='pointer'
              onClick={this._onHideTourClick}
              data-tour-listing-id={id}
            >
              Invalid
            </SrText>
            <br />
            {!isWaitlist && (
              <SrText
                color='blue300'
                cursor='pointer'
                onClick={archiveReasons?.length
                  ? this._onOpenUnArchiveTourListingModal
                  : this._onOpenArchiveTourListingModal
                }
                data-tour-listing-id={id}
              >
                {archiveReasons?.length > 0 ? 'Unarchive' : 'Archive'}
              </SrText>
            )}
          </>
        );
      }

      return {
        cells: lodashCompact([
          {
            content: `${i + 1}`,
          },
          !isWaitlist && {
            content: this._formatArchiveReasons({ archiveReasons, otherArchiveReason }),
          },
          {
            content: user.name,
          },
          {
            content: user.email,
          },
          {
            content: user.phone,
          },
          isWaitlist && {
            content: momentWithAustinTimezone(tour.createdDate).format('MM/DD/YYYY hh:mm A'),
          },
          !isWaitlist && {
            content: momentWithAustinTimezone(tour.startDate).format('MM/DD/YYYY hh:mm A'),
          },
          !isWaitlist && {
            content: tour.type,
          },
          !isWaitlist && {
            content: agentUser.name || '',
          },
          {
            content: listing.addressStreetDisplay,
          },
          {
            content: user.id ? (
              <SrBox>
                <SrLink to={`/admin/tours/all/user/${user.id}`} color='blue300' target='_blank'>
                  Tours
                </SrLink>
                <SrBox width={10} display='inlineBlock' />
                <SrLink to={`/admin/household/all/${user.id}`} color='blue300' target='_blank'>
                  Household
                </SrLink>
              </SrBox>
            ) : null,
          },
          showMessagesColumn && {
            content: (
              <>
                {userFrontConversations.length > 0 && (
                  <SrFrontConversation
                    ctaClassName='tableCell'
                    frontConversation={userFrontConversations[0]}
                    user={user}
                    dispatch={dispatch}
                  />
                )}
              </>
            ),
          },
          {
            content: optionsDOM,
          },
        ]),
      };
    });

    const tourListing = tourListingId
      ? items.find(({ id }) => id === tourListingId)
      : null;

    return (
      <div ref={onInfiniteScrollWrapperRef}>
        <SrTable
          headers={headers}
          rows={rows}
        />
        {tourListing && (
          <SrTourListingFailedModal
            user={tourListing.user}
            listing={tourListing.listing}
            sunlock={tourListing.sunlock?.id ? tourListing.sunlock : null}
            codeboxLock={tourListing.codeboxLock?.id ? tourListing.codeboxLock : null}
            tourListingId={tourListing.id}
            onSubmit={this._onSubmitTourListingFailedModal}
          />
        )}
        {archiveTourListingId && (
          <SrArchiveListingLeadModal
            onClose={this._onCloseArchiveTourListingModal}
            onSubmit={this._onArchiveTourListing}
          />
        )}
        {unArchiveTourListingId && (
          <SrUnArchiveLeadModal
            onClose={this._onCloseUnArchiveTourListingModal}
            onSubmit={this._onUnArchiveTourListing}
          />
        )}
      </div>
    );
  }
}

SrDataTablePartnerStatTours.displayName = 'SrDataTablePartnerStatTours';

export default SrDataTablePartnerStatTours;
